import { z } from 'zod';

export const BANDING_SHAPES = [
  'two-sided',
  'one-sided-low',
  'one-sided-high',
] as const;
export type BandingShape = (typeof BANDING_SHAPES)[number];

export const bandPolaritySchema = z.enum(['good', 'bad']);
export type BandPolarity = z.infer<typeof bandPolaritySchema>;

export const lowerBoundOperatorSchema = z.enum(['gt', 'gte']);
export type LowerBoundOperator = z.infer<typeof lowerBoundOperatorSchema>;

export const upperBoundOperatorSchema = z.enum(['lt', 'lte']);
export type UpperBoundOperator = z.infer<typeof upperBoundOperatorSchema>;

const hexColourSchema = z
  .string()
  .regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a hex colour in #RRGGBB form');

const lowerBoundSchema = z.object({
  operator: lowerBoundOperatorSchema,
  value: z.number().finite(),
});

const upperBoundSchema = z.object({
  operator: upperBoundOperatorSchema,
  value: z.number().finite(),
});

/**
 * A band is bounded by an optional `lower`/`upper` pair — absent means open
 * on that side. A middle band in a full config carries both; the lowest band
 * has no `lower`; the highest has no `upper`.
 */
export const indicatorBandSchema = z
  .object({
    id: z.string().uuid(),
    label: z.string().min(1).max(60),
    colour: hexColourSchema,
    lower: lowerBoundSchema.optional(),
    upper: upperBoundSchema.optional(),
    polarity: bandPolaritySchema,
    severity: z.number().int().min(0),
  })
  .superRefine((band, ctx) => {
    if (!band.lower && !band.upper) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A band must have at least one of lower/upper.',
        path: ['lower'],
      });
      
return;
    }
    if (band.lower && band.upper && band.lower.value >= band.upper.value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'lower.value must be less than upper.value',
        path: ['upper', 'value'],
      });
    }
  });

export type IndicatorBand = z.infer<typeof indicatorBandSchema>;

/**
 * Input to {@link deriveBandPolarities} — a band with everything decided
 * except the derived `polarity`/`severity`, ordered lowest→highest along the
 * value line.
 */
export type OrderedBandInput = Omit<IndicatorBand, 'polarity' | 'severity'>;

export const indicatorBandingConfigSchema = z.object({
  bands: z.array(indicatorBandSchema).min(1),
});

export type IndicatorBandingConfig = z.infer<
  typeof indicatorBandingConfigSchema
>;

/**
 * Index (or, for a two-sided even count, the lower of the two middle indexes)
 * treated as "good" for the given shape and band count.
 */
const goodIndex = (shape: BandingShape, count: number): number => {
  if (shape === 'one-sided-low') {
    return 0;
  }
  if (shape === 'one-sided-high') {
    return count - 1;
  }

  // two-sided: true middle for odd counts, lower-middle for even counts.
  return Math.ceil(count / 2) - 1;
};

/**
 * Fills in `polarity`/`severity` for an ordered (lowest→highest) band list,
 * given which index is "good". Every other band is "bad" with severity equal
 * to its distance (in band-steps) from that index. Shared by
 * {@link deriveBandPolarities} (which derives the good index from `shape`)
 * and {@link buildSeededBandingConfig} (which knows the good index directly
 * from the bound partition it built).
 */
const applyPolarities = (
  bands: OrderedBandInput[],
  atGoodIndex: number
): IndicatorBand[] =>
  bands.map((band, index) => {
    const severity = Math.abs(index - atGoodIndex);

    return {
      ...band,
      polarity: severity === 0 ? 'good' : 'bad',
      severity,
    };
  });

/**
 * Fills in `polarity`/`severity` for an ordered (lowest→highest) band list.
 * Exactly one index is "good" per {@link goodIndex}; every other band is
 * "bad" with severity equal to its distance (in band-steps) from that index.
 */
export const deriveBandPolarities = (
  shape: BandingShape,
  bands: OrderedBandInput[]
): IndicatorBand[] => applyPolarities(bands, goodIndex(shape, bands.length));

const withinLowerBound = (
  value: number,
  lower: IndicatorBand['lower']
): boolean =>
  !lower || (lower.operator === 'gt' ? value > lower.value : value >= lower.value);

const withinUpperBound = (
  value: number,
  upper: IndicatorBand['upper']
): boolean =>
  !upper || (upper.operator === 'lt' ? value < upper.value : value <= upper.value);

const matchesBand = (value: number, band: IndicatorBand): boolean =>
  withinLowerBound(value, band.lower) && withinUpperBound(value, band.upper);

/**
 * Evaluates `bands` in array order and returns the first match. A
 * well-formed config is a non-overlapping partition, so order shouldn't
 * matter — but first-match-wins is kept as the evaluation contract for
 * robustness against a config that (despite {@link validateBandingConfig})
 * has slipped into an inconsistent state. Returns `null` when no band
 * matches (a gap in coverage).
 */
export const evaluateResultBand = (
  value: number,
  bands: IndicatorBand[]
): IndicatorBand | null => bands.find((band) => matchesBand(value, band)) ?? null;

export const BANDING_VALIDATION_ERROR_CODES = [
  'too-few-bands',
  'no-good-centre',
  'gap',
  'overlap',
] as const;
export type BandingValidationErrorCode =
  (typeof BANDING_VALIDATION_ERROR_CODES)[number];

export interface BandingValidationError {
  code: BandingValidationErrorCode;
  message: string;
  bandId?: string;
}

/**
 * Validates a banding config, given `bands` ordered lowest→highest along the
 * value line. Checks are independent — several codes may be returned
 * together. Messages are code-only; the UI translates them.
 *
 * Per-band self-consistency (lower.value < upper.value, at least one of
 * lower/upper present) is enforced by {@link indicatorBandSchema} itself, so
 * it isn't re-checked here — this function only checks properties that span
 * MULTIPLE bands (the partition as a whole).
 *
 * A genuinely out-of-sequence array (bands not actually given lowest→highest)
 * isn't checked for as a distinct condition — any such inversion always
 * shows up as a numeric "crossing" between some adjacent pair, which the
 * pairwise check below already reports as 'overlap'.
 */
export const validateBandingConfig = (
  shape: BandingShape,
  bands: IndicatorBand[]
): BandingValidationError[] => {
  const errors: BandingValidationError[] = [];

  if (bands.length < 2) {
    errors.push({
      code: 'too-few-bands',
      message: 'A banding config needs at least 2 bands.',
    });
  }

  if (shape === 'two-sided' && bands.length < 3) {
    errors.push({
      code: 'no-good-centre',
      message:
        'A two-sided banding config needs at least 3 bands to have a good centre band flanked by bad bands on both sides.',
    });
  }

  bands.forEach((current, index) => {
    if (index === 0 && current.lower) {
      errors.push({
        code: 'gap',
        message: `Band "${current.label}" is the lowest band but has a lower bound; values below it are uncovered.`,
        bandId: current.id,
      });
    }
    if (index === bands.length - 1 && current.upper) {
      errors.push({
        code: 'gap',
        message: `Band "${current.label}" is the highest band but has an upper bound; values above it are uncovered.`,
        bandId: current.id,
      });
    }

    const next = bands[index + 1];
    if (!next) {
      return;
    }

    if (!current.upper || !next.lower) {
      errors.push({
        code: 'overlap',
        message: `Bands "${current.label}" and "${next.label}" are unbounded toward each other and overlap.`,
        bandId: current.id,
      });
      
return;
    }

    const upperInclusive = current.upper.operator === 'lte';
    const lowerInclusive = next.lower.operator === 'gte';

    if (current.upper.value === next.lower.value) {
      if (upperInclusive && lowerInclusive) {
        errors.push({
          code: 'overlap',
          message: `Bands "${current.label}" and "${next.label}" both claim ${current.upper.value}.`,
          bandId: current.id,
        });
      } else if (!upperInclusive && !lowerInclusive) {
        errors.push({
          code: 'gap',
          message: `Bands "${current.label}" and "${next.label}" both exclude ${current.upper.value} — it is uncovered.`,
          bandId: current.id,
        });
      }
      // One inclusive, one exclusive at the same value = a clean boundary.
    } else if (current.upper.value < next.lower.value) {
      errors.push({
        code: 'gap',
        message: `A gap exists between "${current.label}" (ends ${current.upper.value}) and "${next.label}" (starts ${next.lower.value}).`,
        bandId: current.id,
      });
    } else {
      errors.push({
        code: 'overlap',
        message: `Bands "${current.label}" and "${next.label}" overlap between ${next.lower.value} and ${current.upper.value}.`,
        bandId: current.id,
      });
    }
  });

  return errors;
};

export interface SeededBandingBounds {
  lowerTolerance: number | null;
  lowerAppetite: number | null;
  upperAppetite: number | null;
  upperTolerance: number | null;
}

export interface SeededBandingLabels {
  withinAppetite: string;
  outsideAppetite: string;
  outsideTolerance: string;
}

export interface SeededBandingColours {
  withinAppetite: string;
  outsideAppetite: string;
  outsideTolerance: string;
}

export interface BuildSeededBandingConfigInput {
  bounds: SeededBandingBounds;
  labels: SeededBandingLabels;
  colours: SeededBandingColours;
}

type BoundIdentity =
  | 'lowerTolerance'
  | 'lowerAppetite'
  | 'upperAppetite'
  | 'upperTolerance';

const LOWER_IDENTITIES: readonly BoundIdentity[] = [
  'lowerTolerance',
  'lowerAppetite',
];
const UPPER_IDENTITIES: readonly BoundIdentity[] = [
  'upperAppetite',
  'upperTolerance',
];

interface Survivor {
  identity: BoundIdentity;
  value: number;
}

/**
 * Walks the 4 bounds in nominal order [LT, LA, UA, UT], skipping nulls, and
 * defensively drops any bound that doesn't strictly increase on the
 * previously-accepted value — including an exact tie (the "equal-adjacent
 * bounds" case, e.g. LT === LA, collapses to just the outer one). The web
 * form + external API both enforce monotonic bounds, so a violation reaching
 * here is legacy-arbitrary data; dropping deterministically (rather than
 * trying to replicate undefined behaviour) is the documented trade-off — see
 * {@link buildSeededBandingConfig}'s doc comment.
 */
const collectSurvivors = (bounds: SeededBandingBounds): Survivor[] => {
  const nominal: { identity: BoundIdentity; value: number | null }[] = [
    { identity: 'lowerTolerance', value: bounds.lowerTolerance },
    { identity: 'lowerAppetite', value: bounds.lowerAppetite },
    { identity: 'upperAppetite', value: bounds.upperAppetite },
    { identity: 'upperTolerance', value: bounds.upperTolerance },
  ];

  const survivors: Survivor[] = [];
  nominal.forEach(({ identity, value }) => {
    if (value === null) {
      return;
    }
    const last = survivors[survivors.length - 1];
    if (last !== undefined && value <= last.value) {
      return;
    }
    survivors.push({ identity, value });
  });

  return survivors;
};

const categoryForIdentity = (
  identity: BoundIdentity
): 'outsideTolerance' | 'outsideAppetite' =>
  identity === 'lowerTolerance' || identity === 'upperTolerance'
    ? 'outsideTolerance'
    : 'outsideAppetite';

interface Segment {
  category: 'outsideTolerance' | 'outsideAppetite' | 'within';
  lower?: { operator: LowerBoundOperator; value: number };
  upper?: { operator: UpperBoundOperator; value: number };
}

/**
 * Builds the ordered (lowest→highest) segment partition from surviving
 * boundaries. Boundary inclusivity replicates
 * `calculateConformanceRating` exactly: for a LOWER-side boundary (tolerance
 * or appetite), the segment ABOVE owns the exact value (`lt` below / `gte`
 * above); for an UPPER-side boundary, the segment BELOW owns it (`lte` below
 * / `gt` above). The "within" segment is whatever sits between the
 * innermost surviving lower-side bound (or open, if none survived) and the
 * innermost surviving upper-side bound (or open) — so e.g. with only
 * `upperTolerance` set, "within" is everything below it, not just a
 * conventional appetite band.
 */
const buildSegments = (survivors: Survivor[]): Segment[] => {
  const lowerSurvivors = survivors.filter((s) =>
    LOWER_IDENTITIES.includes(s.identity)
  );
  const upperSurvivors = survivors.filter((s) =>
    UPPER_IDENTITIES.includes(s.identity)
  );

  const segments: Segment[] = [];

  lowerSurvivors.forEach((survivor, index) => {
    const previous = lowerSurvivors[index - 1];
    segments.push({
      category: categoryForIdentity(survivor.identity),
      ...(previous
        ? { lower: { operator: 'gte' as const, value: previous.value } }
        : {}),
      upper: { operator: 'lt', value: survivor.value },
    });
  });

  const withinLower = lowerSurvivors[lowerSurvivors.length - 1];
  const withinUpper = upperSurvivors[0];
  segments.push({
    category: 'within',
    ...(withinLower
      ? { lower: { operator: 'gte' as const, value: withinLower.value } }
      : {}),
    ...(withinUpper
      ? { upper: { operator: 'lte' as const, value: withinUpper.value } }
      : {}),
  });

  upperSurvivors.forEach((survivor, index) => {
    const next = upperSurvivors[index + 1];
    segments.push({
      category: categoryForIdentity(survivor.identity),
      lower: { operator: 'gt', value: survivor.value },
      ...(next
        ? { upper: { operator: 'lte' as const, value: next.value } }
        : {}),
    });
  });

  return segments;
};

const labelAndColourFor = (
  category: Segment['category'],
  labels: SeededBandingLabels,
  colours: SeededBandingColours
): { label: string; colour: string } => {
  if (category === 'within') {
    return { label: labels.withinAppetite, colour: colours.withinAppetite };
  }
  if (category === 'outsideAppetite') {
    return { label: labels.outsideAppetite, colour: colours.outsideAppetite };
  }

  return { label: labels.outsideTolerance, colour: colours.outsideTolerance };
};

/**
 * Builds the seeded banding config for an indicator's legacy
 * four-bound thresholds, as an ORDERED, non-overlapping PARTITION —
 * replicating `calculateConformanceRating`'s `conformanceIndicatorRating`
 * exactly for any MONOTONIC bound combination (including partial sets, which
 * collapse the "within" segment to whatever the surviving innermost bounds
 * leave open — see {@link buildSegments}).
 *
 * Bound sets are defensively required to be strictly increasing in nominal
 * order [lowerTolerance, lowerAppetite, upperAppetite, upperTolerance]; a
 * non-monotonic (legacy-arbitrary) input has any violating bound
 * deterministically dropped by {@link collectSurvivors} rather than
 * reproduced via the legacy function's check-order semantics — the web form
 * and external API both already enforce monotonic bounds, so this only
 * matters for pre-existing/imported data.
 *
 * `polarity`/`severity` are derived positionally (band-steps from the
 * "within" segment) via the same {@link applyPolarities} helper
 * {@link deriveBandPolarities} uses, and `shape` is then derived from where
 * that segment landed so the two agree: a partial seed whose "within" segment
 * sits at either end is `one-sided-low`/`one-sided-high`, not `two-sided`.
 * Keeping them in step matters twice over — a `two-sided` stamp on a 2-band
 * seed fails {@link validateBandingConfig}'s `no-good-centre` rule, and the
 * editor re-derives polarity from `shape` on open, which would silently move
 * which band is good.
 *
 * Returns `null` when all four bounds are null (legacy: `NotSet`).
 */
export const buildSeededBandingConfig = (
  { bounds, labels, colours }: BuildSeededBandingConfigInput,
  idGenerator: () => string = () => crypto.randomUUID()
): { shape: BandingShape; bands: IndicatorBand[] } | null => {
  if (
    bounds.lowerTolerance === null &&
    bounds.lowerAppetite === null &&
    bounds.upperAppetite === null &&
    bounds.upperTolerance === null
  ) {
    return null;
  }

  const segments = buildSegments(collectSurvivors(bounds));
  const withinIndex = segments.findIndex(
    (segment) => segment.category === 'within'
  );

  const orderedBands: OrderedBandInput[] = segments.map((segment) => ({
    id: idGenerator(),
    ...labelAndColourFor(segment.category, labels, colours),
    ...(segment.lower ? { lower: segment.lower } : {}),
    ...(segment.upper ? { upper: segment.upper } : {}),
  }));

  // Shape has to follow where "within" actually landed, not default to
  // 'two-sided'. A partial seed has no band on one side, so a 'two-sided'
  // stamp both fails validateBandingConfig's `no-good-centre` rule and makes
  // the editor's re-derive-from-shape move which band is good. Mirrors the
  // CUS-185 backfill's own derivation, which keys on the same two counts.
  const shape: BandingShape =
    withinIndex === 0
      ? 'one-sided-low'
      : withinIndex === segments.length - 1
        ? 'one-sided-high'
        : 'two-sided';

  return {
    shape,
    bands: applyPolarities(orderedBands, withinIndex),
  };
};
